import * as React from 'react';

import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTrigger,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import * as facilityGroupFormLocalState from '../../_state/facilityGroupForm.localState';

import { FacilityGroupForm } from './FacilityGroupForm';

import { useTranslations } from '@/facilities/i18n/useTranslations';
import { useCreateFacilityGroupMutation } from '~/modules/facilities/hooks/mutations/useCreateFacilityGroupMutation';
import { useUpdateFacilityGroupMutation } from '~/modules/facilities/hooks/mutations/useUpdateFacilityGroupMutation';
import { FacilityGroupRecord } from '~/modules/facilities/types/facilityGroup';

interface FacilityGroupDialogProps {
  group?: FacilityGroupRecord;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const FacilityGroupDialog = ({
  group,
  isOpen,
  onClose,
  onSuccess,
}: FacilityGroupDialogProps) => {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: createGroup, isPending: isCreating } =
    useCreateFacilityGroupMutation();
  const { mutateAsync: updateGroup, isPending: isUpdating } =
    useUpdateFacilityGroupMutation();
  const formData = useAtomValue(facilityGroupFormLocalState.$facilityGroupForm);
  const resetForm = useSetAtom(
    facilityGroupFormLocalState.$resetFacilityGroupForm,
  );
  const updateForm = useSetAtom(
    facilityGroupFormLocalState.$updateFacilityGroupForm,
  );

  React.useEffect(() => {
    if (isOpen && group) {
      updateForm({
        name: group.name,
        facilityIds: group.facilities.map((f) => f.id),
      });
    }
  }, [isOpen, group, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      if (group) {
        await updateGroup({
          id: group.id,
          ...formData,
        });
        toast.success(t.facilityGroups.dialog.updateSuccess);
      } else {
        await createGroup(formData);
        toast.success(t.facilityGroups.dialog.createSuccess);
      }
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(t.facilityGroups.dialog.genericError);
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleClose}>
      <DialogOverlay className="@FacilityGroupDialog">
        <DialogContent position="center">
          <DialogHeader>
            <div className="text-xl font-semibold">
              {group
                ? t.facilityGroups.dialog.editTitle
                : t.facilityGroups.dialog.createTitle}
            </div>
          </DialogHeader>

          <div className="p-4">
            <FacilityGroupForm isEditing={!!group} />
          </div>

          <DialogFooter>
            <Button
              color="failure"
              variant="outline"
              isPending={isCreating || isUpdating}
              onPress={handleClose}
            >
              {t.facilityGroups.dialog.actions.cancel}
            </Button>
            <Button
              color="primary"
              variant="solid"
              isPending={isCreating || isUpdating}
              isLoading={isCreating || isUpdating}
              onPress={handleSubmit}
            >
              {group
                ? t.facilityGroups.dialog.actions.update
                : t.facilityGroups.dialog.actions.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogOverlay>
    </DialogTrigger>
  );
};
